import { makeAutoObservable } from 'mobx';
import { enableStaticRendering } from 'mobx-react-lite';
import router from 'next/router';
import { CustomFieldType } from '../../components/CustomField/CustomField.type';
import { TicketFormValues } from '../../components/GrispiTicketForm';
import { SystemFieldPrefixMap } from '../../constants/forms/ticketFormFields';
import { AssigneeCascaderOptionType } from '../../hooks/ticketDetailPage/useCascaderAssigneeList';
import {
    Assignee,
    CustomFieldsKeyType,
    NewTicket,
    SimpleUser,
    TicketFieldsKeyType,
    TicketSystemFieldsKey
} from '../../model/types';
import { getCurrentUserEmail, getCurrentUserFullName, getCurrentUserPhone } from '../../utils/accountHelper';
import {
    controlledSerializedValue,
    parseEmailFromSerializedValue,
    parseFullNameFromUserFriendlyValue,
    parsePhoneNumberFromSerializedValue,
    parseSerializedValueFromUserFormValue,
    parseUserFriendlyValueFromUserValue
} from '../../utils/forms/formHelper.parser';
import { generateTicketFormName } from '../../utils/forms/formHelpers';
import { isSSR } from '../../utils/helpers';
import { getMacroStore } from '../../utils/store/getStore';
import { getTicketDescription, getTicketKey, getTicketSubject } from '../../utils/ticketHelper';

enableStaticRendering(isSSR());

export type UserForPlaceholder = Pick<SimpleUser, 'email' | 'fullName' | 'phone'>;
enum userInfoKeyTransformer {
    'email' = 'email',
    'phone' = 'phone',
    'full_name' = 'fullName'
}
type infoKeyType = keyof typeof userInfoKeyTransformer;

// below values are hardoced keys, other keys will be collected from fieldMap values of ticketDetail
enum BasePlaceholdersMap {
    'ticket.key' = 'ticket.key',
    'ticket.url' = 'ticket.url',
    'ticket.description' = 'ticket.description',
    'ticket.subject' = 'ticket.subject',
    'current_user.email' = 'current_user.email',
    'current_user.full_name' = 'current_user.full_name',
    'current_user.phone' = 'current_user.phone'
}

export type basePlaceholder = keyof typeof BasePlaceholdersMap;
export type IBasePlaceholder = Record<basePlaceholder, string>;

type ticket = 'ticket';

type userPlaceholderKeyType = 'email' | 'full_name' | 'phone';
type userSystemFieldPlaceholderKeyType = `${ticket}.${string}.${userPlaceholderKeyType}`;
type userCustomFieldPlaceholderKeyType = `${ticket}.cf.${string}.${userPlaceholderKeyType}`;
type UserFieldPlaceholderKey =
    | userSystemFieldPlaceholderKeyType
    | userCustomFieldPlaceholderKeyType
    | `${ticket}.group.name`;
export type TicketFieldPlaceholderKey = `${ticket}.${string}` | UserFieldPlaceholderKey;
export type IFieldPlaceholder = Record<TicketFieldPlaceholderKey, string | number>;

export type IPlaceholder = IBasePlaceholder & IFieldPlaceholder;

export const USER_ASSIGNEE = 'assignee';

// base placeholders value helpers i.e ticket.key, ticket.url ticket.description
const getTicketURL: () => string = () => (!isSSR() ? window.location.href : null);

const deriveBasePlaceholderValue: (ticket: NewTicket, placeholderKey: basePlaceholder) => string = (
    ticket,
    placeholderKey
) => {
    if (placeholderKey === 'ticket.key') return getTicketKey(ticket);
    if (placeholderKey === 'ticket.url') return getTicketURL();
    if (placeholderKey === 'ticket.subject') return getTicketSubject(ticket);
    if (placeholderKey === 'ticket.description') return getTicketDescription(ticket);
    if (placeholderKey === 'current_user.email') return getCurrentUserEmail();
    if (placeholderKey === 'current_user.full_name') return getCurrentUserFullName();
    if (placeholderKey === 'current_user.phone') return getCurrentUserPhone();

    // console.debug(`Placeholder ${placeholderKey} is not supported`);
    return null;
};

const generateBasePlaceholders: (ticketDetail: NewTicket) => IBasePlaceholder = (ticketDetail) => {
    const newValues = Object.keys(BasePlaceholdersMap).reduce((acc, placeholderKey: basePlaceholder) => {
        const placeholderValue = deriveBasePlaceholderValue(ticketDetail, placeholderKey);

        return { ...acc, [placeholderKey]: placeholderValue };
    }, {} as IBasePlaceholder);
    return newValues;
};

export const getRelatedUserInfo =
    (user: UserForPlaceholder) =>
    (infoKey: infoKeyType): string =>
        user?.[userInfoKeyTransformer[infoKey]];

export const deriveSimplePlaceholderValue: (fieldKey: TicketFieldsKeyType, ticket: NewTicket) => string = (
    fieldKey,
    ticket
) => ticket?.fieldMap?.[fieldKey]?.value as string;
export const deriveUserPlaceholderValue: (
    placeholderKey: UserFieldPlaceholderKey,
    user: UserForPlaceholder
) => string = (placeholderKey, user) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const infoKey = placeholderKey.split('.')[2] as infoKeyType;
    return getRelatedUserInfo(user)(infoKey);
};

export const deriveUserPlaceholderValueFromSerializedFormValue: (
    placeholderKey: UserFieldPlaceholderKey,
    serializedFormValue: string
) => string = (placeholderKey, serializedFormValue) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const infoKey = placeholderKey.split('.')[2] as infoKeyType;
    if (infoKey === 'email')
        return (
            parseEmailFromSerializedValue(parseSerializedValueFromUserFormValue(serializedFormValue)) || ''
        ).trimEnd();
    if (infoKey === 'phone')
        return (
            parsePhoneNumberFromSerializedValue(parseSerializedValueFromUserFormValue(serializedFormValue)) || ''
        ).trimEnd();
    if (infoKey === 'full_name')
        return (
            parseFullNameFromUserFriendlyValue(parseUserFriendlyValueFromUserValue(serializedFormValue)) || ''
        ).trimEnd();
};

export const deriveGroupPlaceholderValue: (value: string) => string = (value) => {
    const groupId = value.split(':')[0];
    return getMacroStore().getGroupInfo(+groupId);
};

export const deriveAssigneePlaceholderValue: (placeholderKey: UserFieldPlaceholderKey, value: string) => string = (
    placeholderKey,
    value
) => {
    // const groupId = value.split(':')[0];
    const userId = value.split(':')[1];

    const user = getMacroStore().getAssigneeInfo(+userId);
    return deriveUserPlaceholderValue(placeholderKey, user);
};

const getPrefix: (fieldKey: TicketFieldsKeyType) => 'ticket' | 'ticket.cf' = (fieldKey) =>
    isSystemField(fieldKey) ? 'ticket' : 'ticket.cf';
const fixFieldKey: (fieldKey: TicketFieldsKeyType) => string = (fieldKey) =>
    isSystemField(fieldKey) ? fieldKey.replace(SystemFieldPrefixMap.TICKET, '') : fieldKey;

const generateUserPlaceholderKeys: (fieldKey: TicketFieldsKeyType) => UserFieldPlaceholderKey[] = (fieldKey) => {
    const prefix = getPrefix(fieldKey);
    const fixedFieldKey = fixFieldKey(fieldKey);

    return Object.keys(userInfoKeyTransformer).map(
        (userInfoKey: infoKeyType) => `${prefix}.${fixedFieldKey}.${userInfoKey}`
    ) as UserFieldPlaceholderKey[];
};

const generateAssigneePlaceholderKeys: (fieldKey: TicketFieldsKeyType) => UserFieldPlaceholderKey[] = (fieldKey) => {
    const prefix = getPrefix(fieldKey);

    const groupNamePlaceholderKey = `${prefix}.group.name`;
    const basicUserPlaceholderKeys = generateUserPlaceholderKeys(fieldKey);

    return [groupNamePlaceholderKey, ...basicUserPlaceholderKeys] as UserFieldPlaceholderKey[];
};
const generatePlaceholderKey: (fieldKey: TicketFieldsKeyType) => `${ticket}.${string}` = (fieldKey) => {
    const prefix = getPrefix(fieldKey);
    const fixedFieldKey = fixFieldKey(fieldKey);

    return `${prefix}.${fixedFieldKey}`;
};

const placeholderKeyFactoryForSystemFields: (
    fieldKey: TicketSystemFieldsKey
) => TicketFieldPlaceholderKey | TicketFieldPlaceholderKey[] = (fieldKey) => {
    if (fieldKey === 'ts.assignee') {
        return generateAssigneePlaceholderKeys(fieldKey);
    }

    if (fieldKey === 'ts.requester') {
        return generateUserPlaceholderKeys(fieldKey);
    }

    if (fieldKey === 'ts.priority') {
        return generatePlaceholderKey(fieldKey);
    }

    if (fieldKey === 'ts.status') {
        return generatePlaceholderKey(fieldKey);
    }

    if (fieldKey === 'ts.subject') {
        return generatePlaceholderKey(fieldKey);
    }

    if (fieldKey === 'ts.type') {
        return generatePlaceholderKey(fieldKey);
    }

    if (fieldKey === 'ts.description') {
        // this field is used for end-user-form and not related with ticket.description placeholder
        return;
    }

    // unmaintained: ts.attachments, ts.creator, ts.email_ccs, ts.followers, ts.tags
    return;
};
const placeholderKeyFactoryForCustomFields: (
    fieldKey: CustomFieldsKeyType,
    fieldType: CustomFieldType
) => TicketFieldPlaceholderKey | TicketFieldPlaceholderKey[] = (fieldKey, fieldType) => {
    if (!fieldType) {
        // console.debug('placeholderKeyFactoryForCustomFields: fieldType is empty');
        return;
    }

    if (fieldType === 'USER') {
        return generateUserPlaceholderKeys(fieldKey);
    }

    if (fieldType === 'NUMBER_DECIMAL') {
        return generatePlaceholderKey(fieldKey);
    }

    if (fieldType === 'NUMBER_INTEGER') {
        return generatePlaceholderKey(fieldKey);
    }

    if (fieldType === 'TEXT') {
        return generatePlaceholderKey(fieldKey);
    }

    if (fieldType === 'SELECT') {
        return generatePlaceholderKey(fieldKey);
    }

    // unmaintained: MULTI_SELECT, MULTI_USER, DATE, DATE_TIME, TAG
    return;
};
const isSystemField = (fieldKey: TicketFieldsKeyType): boolean => fieldKey.startsWith(SystemFieldPrefixMap.TICKET);
const placeholderKeyFactory: (
    fieldKey: TicketFieldsKeyType,
    fieldType: CustomFieldType
) => TicketFieldPlaceholderKey | TicketFieldPlaceholderKey[] = (fieldKey, fieldType) => {
    return isSystemField(fieldKey)
        ? placeholderKeyFactoryForSystemFields(fieldKey as TicketSystemFieldsKey)
        : placeholderKeyFactoryForCustomFields(fieldKey as CustomFieldsKeyType, fieldType);
};
// const placeholderKeyFactoryFromFormValues: (
//     fieldKey: TicketFieldsKeyType,
// ) => TicketFieldPlaceholderKey | TicketFieldPlaceholderKey[] = (fieldKey) => {
//     return isSystemField(fieldKey)
//         ? placeholderKeyFactoryForSystemFields(fieldKey as TicketSystemFieldsKey)
//         : // TODO cagri: currentky, cannot know FormValues fieldKeyType, should handle later
//           generatePlaceholderKey(fieldKey);
// };

// currently only supports single-value-fields' placeholder values
export class MacroStore {
    placeholderValues: Record<string, IPlaceholder>;
    assigneeOptions: AssigneeCascaderOptionType[];

    constructor() {
        this.placeholderValues = {};
        this.assigneeOptions = [];
        // Call it here
        makeAutoObservable(this);
    }
    getGroupInfo: (groupId: number) => string = (groupId) => {
        const groupName = this.assigneeOptions.find(({ value }) => value === groupId)?.label;

        return groupName;
    };
    getAssigneeInfo: (userId: number) => UserForPlaceholder = (userId) => {
        const assignee = this.assigneeOptions
            .flatMap(({ children }) => children)
            .find(({ value }) => value === userId)?.userinfo;
        return assignee;
    };
    setAssigneeOptions: (assigneeOptions: AssigneeCascaderOptionType[]) => void = (assigneeOptions) => {
        this.assigneeOptions = assigneeOptions;
    };
    getPlaceholderValue: (placeholderKey: TicketFieldPlaceholderKey) => string | number = (placeholderKey) => {
        if (!placeholderKey) {
            // console.debug('MacroStore getPlaceholderValue: given placeholderKey is empty');
            return;
        }

        const formName = generateTicketFormName(router.pathname, router.query);

        return this.placeholderValues?.[formName]?.[placeholderKey as TicketFieldPlaceholderKey];
    };
    setPlaceholderValue: <T extends CustomFieldType>(
        fieldKey: TicketFieldsKeyType,
        fieldType: T,
        newValue: string | number,
        assigneeFormValue?: TicketFormValues['fields']
    ) => void = (fieldKey, fieldType, newValue) => {
        if (!fieldKey) {
            // console.debug('MacroStore setPlaceholderValue: fieldKey is empty');
            return;
        }

        const placeholderKey = placeholderKeyFactory(fieldKey, fieldType);
        if (!placeholderKey) {
            // console.debug(
            //     `MacroStore setPlaceholderValue: placeholderKey is not available currently for fieldKey: ${fieldKey}`
            // );
            return;
        }

        const formName = generateTicketFormName(router.pathname, router.query);
        if (!formName) {
            return;
        }

        if (Array.isArray(placeholderKey)) {
            const newValues = {} as Record<UserFieldPlaceholderKey, string>;
            (placeholderKey as UserFieldPlaceholderKey[]).forEach((key) => {
                if (key === 'ticket.group.name') {
                    newValues[key] = newValue === null ? '' : deriveGroupPlaceholderValue(newValue.toString());
                    return;
                }
                if (key === 'ticket.assignee.full_name') {
                    newValues[key] = newValue === null ? '' : deriveAssigneePlaceholderValue(key, newValue.toString());
                    return;
                }
                if (key === 'ticket.assignee.email') {
                    newValues[key] = newValue === null ? '' : deriveAssigneePlaceholderValue(key, newValue.toString());
                    return;
                }
                if (key === 'ticket.assignee.phone') {
                    newValues[key] = newValue === null ? '' : deriveAssigneePlaceholderValue(key, newValue.toString());
                    return;
                }

                newValues[key] =
                    newValue === null
                        ? ''
                        : deriveUserPlaceholderValueFromSerializedFormValue(
                              key,
                              controlledSerializedValue(newValue.toString())
                          );
            });

            this.setPlaceholderValues(formName, {
                ...this.placeholderValues?.[formName],
                ...newValues
            });
            return;
        }

        if (!this.placeholderValues[formName]) {
            this.placeholderValues[formName] = {} as IPlaceholder;
        }
        this.placeholderValues[formName][placeholderKey] = newValue;
    };

    // @forceReset: this params is used not to extend but to replace placeholderValues with new values,
    setPlaceholderValues: (formName: string, newValues: IPlaceholder, forceReset?: boolean) => void = (
        formName,
        newValues,
        forceReset = false
    ) => {
        if (forceReset) {
            this.placeholderValues[formName] = newValues;
            return;
        }
        this.placeholderValues[formName] = { ...this.placeholderValues[formName], ...newValues };
    };

    initPlaceholderValues: (ticketDetail: NewTicket) => void = (ticketDetail) => {
        if (!ticketDetail) {
            // console.debug('MacroStore initPlaceholderValues: ticketDetail is not available');
            return;
        }

        const basePlaceholderValues = generateBasePlaceholders(ticketDetail);

        const formName = generateTicketFormName(router.pathname, router.query);

        if (!ticketDetail?.fieldMap) {
            // console.debug('MacroStore initPlaceholderValues: fieldMap of ticketDetail is empty');
            // this.setPlaceholderValues(basePlaceholderValues);
            this.setPlaceholderValues(formName, basePlaceholderValues, true);
        }

        const { fieldMap: fieldList } = ticketDetail;

        const newValues = Object.entries(fieldList).reduce((acc, [fieldKey, field]) => {
            if (!fieldKey) {
                // console.debug(`MacroStore initPlaceholderValues: fieldKey is empty`);
                return acc;
            }

            if (!field?.key) {
                // console.debug(`MacroStore initPlaceholderValues: field definition is empty for fieldKey: ${fieldKey}`);
                return acc;
            }

            const placeholderKey = placeholderKeyFactory(fieldKey as TicketFieldsKeyType, field?.type);

            if (!placeholderKey) {
                // console.debug(
                //     `MacroStore initPlaceholderValues: placeholderKey is not available currently  for fieldKey: ${fieldKey}`
                // );
                return acc;
            }

            if (Array.isArray(placeholderKey)) {
                const newValues = {} as Record<UserFieldPlaceholderKey, string>;
                (placeholderKey as UserFieldPlaceholderKey[]).forEach((key) => {
                    if (key === 'ticket.group.name') {
                        newValues[key] = (field?.value as Assignee)?.group?.name;
                        return;
                    }
                    if (key === 'ticket.assignee.full_name') {
                        newValues[key] = (field?.value as Assignee)?.user?.fullName;
                        return;
                    }
                    if (key === 'ticket.assignee.email') {
                        newValues[key] = (field?.value as Assignee)?.user?.email;
                        return;
                    }
                    if (key === 'ticket.assignee.phone') {
                        newValues[key] = (field?.value as Assignee)?.user?.phone;
                        return;
                    }

                    newValues[key] = deriveUserPlaceholderValue(key, field?.value as UserForPlaceholder);
                });

                return { ...acc, ...newValues };
            }

            const placeholderValue = deriveSimplePlaceholderValue(fieldKey as TicketFieldsKeyType, ticketDetail);

            return { ...acc, [placeholderKey]: placeholderValue ?? null };
        }, {} as IPlaceholder);

        this.setPlaceholderValues(formName, { ...basePlaceholderValues, ...newValues }, true);
    };

    initNewTicketPlaceholderValues: () => void = () => {
        const newValues: Partial<IPlaceholder> = {
            'ticket.url': getTicketURL(),
            'ticket.requester.full_name': getCurrentUserFullName(),
            'ticket.requester.email': getCurrentUserEmail(),
            'ticket.requester.phone': getCurrentUserPhone(),
            'ticket.status': 'New',
            'current_user.email': getCurrentUserEmail(),
            'current_user.full_name': getCurrentUserFullName(),
            'current_user.phone': getCurrentUserPhone()
        };

        const formName = generateTicketFormName(router.pathname, router.query);

        this.setPlaceholderValues(formName, newValues as any, true);
    };

    removePlaceholderValuesOfCurrentPage: () => void = () => {
        const formName = generateTicketFormName(router.pathname, router.query);

        delete this.placeholderValues[formName];
    };
}
